/**
 * The ladder: who is above whom, and what a result does to that.
 *
 * A fight has had a winner since step 30 and it has never been worth anything.
 * This is the thing a win is *for* — a ranked list with the player somewhere in
 * it, which moves when they fight and keeps moving when they do not.
 *
 * Pure, like everything a rule lives in: no three.js, no renderer, no DOM. The
 * whole ladder can be run for a simulated year in a unit test, which is the
 * only way to find out whether the churn settles into a sensible shape or
 * grinds everybody into the same order and stops.
 *
 * **One movement rule, and it applies to everybody.** The player is not a
 * special case in here — they are a roster entry with a flag on it, and the
 * same function settles their fights and the ones happening across the city
 * while they are in the ring. A ladder where the player's results obey
 * different arithmetic from everybody else's is a ladder that will drift out of
 * agreement with itself the first time somebody adds a rule to one of them.
 */

import FILE from './ladder.json';
import { VENUES, createVenues, validateVenues } from './venues.js';
import { purseFor } from './cards.js';
import BANK from './bank.json';
import { validateLoadout } from './bank.js';
import { STATS, STYLES } from '../core/styles.js';
import { tierNames } from '../core/difficulty.js';
import { validateRingcraft } from '../core/ringcraft.js';
import { meshFor } from '../fighters/meshes.js';

export class LadderConfigError extends Error {}

/**
 * How far apart two tints must be — CIE76 ΔE in Lab — for the player and
 * whoever is booked against them. Both wear the same mesh, so colour is the
 * only thing telling them apart, and it is softened on the way to the screen
 * (a textured body is pulled toward its tint, not painted). Around 2 is the
 * smallest difference anybody can see side by side; 50 is a different colour
 * at a glance across a platform. The closest pair on the shipped roster is
 * the player's blue against Verse's magenta, at 62.7.
 */
export const MIN_TINT_DISTANCE = 50;

const HEX = /^#[0-9a-f]{6}$/i;

/** CIE76 ΔE between two "#rrggbb" colours: straight-line distance in Lab. */
export function tintDistance(a, b) {
  const [l1, a1, b1] = lab(a);
  const [l2, a2, b2] = lab(b);
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}

/** sRGB "#rrggbb" to CIE Lab, D65. */
function lab(hex) {
  const linear = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const [r, g, b] = linear;
  const x = (0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047;
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const z = (0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883;
  const f = (v) => (v > 0.008856 ? Math.cbrt(v) : 7.787 * v + 16 / 116);
  return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
}

/**
 * Throws on a roster that cannot mean what it says.
 *
 * The type checks are the cheap half. The ones that matter are about the shape
 * of the *campaign*: a contender threshold that admits everybody is not a gate,
 * and a player who starts inside it has no circuit to fight.
 */
export function validateLadder(config = LADDER) {
  const { roster, playerId, contender, act, movement, churn, campaign, spread, debut } = config ?? {};

  const band = spread ?? { min: 1, max: 1 };
  if (!(band.min > 0) || !(band.max >= band.min)) {
    throw new LadderConfigError(`spread bounds are ${JSON.stringify(spread)}; want { min, max }`);
  }

  if (!Array.isArray(roster) || roster.length < 3) {
    throw new LadderConfigError(`ladder has ${roster?.length ?? 0} fighters; want at least 3`);
  }

  const seen = new Set();
  for (const entry of roster) {
    if (!entry?.id || typeof entry.id !== 'string') {
      throw new LadderConfigError(`a roster entry has an id of ${JSON.stringify(entry?.id)}`);
    }
    if (seen.has(entry.id)) throw new LadderConfigError(`"${entry.id}" is in the roster twice`);
    seen.add(entry.id);
    if (!entry.name) throw new LadderConfigError(`"${entry.id}" has no name`);

    // Where they are from. Not a rule — nothing in the fight reads it — but the
    // screens do, and a fighter the card cannot place is a fighter the card
    // cannot introduce. Checked as a non-empty string rather than against a
    // list of the five boroughs, because half this roster gives a neighbourhood
    // (Harlem) rather than the borough it sits in, and a validator that refused
    // that would be enforcing a geography the file never claimed.
    if (typeof entry.borough !== 'string' || !entry.borough.trim()) {
      throw new LadderConfigError(`"${entry.id}" is from ${JSON.stringify(entry.borough)}`);
    }

    // What they wear in the ring. Everybody has one, the player included: the
    // two corners are the same mesh, and a fighter with no colour of their own
    // would be drawn in whatever the last person in that slot wore.
    if (typeof entry.tint !== 'string' || !HEX.test(entry.tint)) {
      throw new LadderConfigError(`"${entry.id}" wears ${JSON.stringify(entry.tint)}; want "#rrggbb"`);
    }

    // What they walk out in (step 94): a tier per slot, out of the same shop
    // the player buys from. The player's own is what the bank says they are
    // wearing, so a roster loadout on them would be a second answer.
    if (entry.player && entry.gear !== undefined) {
      throw new LadderConfigError(`the player's gear is what they buy; the roster gives them none`);
    }
    try {
      validateLoadout(entry.gear, { where: `"${entry.id}"` });
    } catch (error) {
      throw new LadderConfigError(error.message);
    }

    if (!Array.isArray(entry.styles) || entry.styles.length === 0) {
      throw new LadderConfigError(`"${entry.id}" fights no styles`);
    }
    for (const style of entry.styles) {
      // A style nobody has heard of is a fighter who cannot be built.
      if (!STYLES.styles[style]) {
        throw new LadderConfigError(`"${entry.id}" fights "${style}", which is not a style`);
      }
    }
    if (entry.styles.length > STYLES.maxStyles) {
      throw new LadderConfigError(
        `"${entry.id}" carries ${entry.styles.length} styles; the cap is ${STYLES.maxStyles}`
      );
    }

    // A fighter's own deviation from what their styles give them. Bounded,
    // because the roster is where somebody would reach for a champion who hits
    // twice as hard rather than one who is harder to beat — and a fighter the
    // player cannot hurt is not a difficulty setting, it is a different game.
    if (entry.spread !== undefined) {
      if (!entry.spread || typeof entry.spread !== 'object') {
        throw new LadderConfigError(`"${entry.id}" has a spread of ${JSON.stringify(entry.spread)}`);
      }
      for (const [stat, value] of Object.entries(entry.spread)) {
        if (!(stat in STATS)) {
          throw new LadderConfigError(
            `"${entry.id}" spreads "${stat}", which is not a stat; there is ${Object.keys(STATS).join(', ')}`
          );
        }
        if (!(value >= band.min && value <= band.max)) {
          throw new LadderConfigError(
            `"${entry.id}" spreads ${stat} to ${value}; the band is ${band.min}..${band.max}`
          );
        }
      }
    }

    // What they do with the room they are standing in, when they do anything.
    // Absent is everybody: a fighter who does not use the arena is the default
    // and needs no entry, and a `ringcraft` block where every number is zero is
    // refused rather than read as one (see `validateRingcraft`).
    validateRingcraft(entry.ringcraft, { where: `"${entry.id}"` });

    // Everybody the player can be matched against has to be drivable, and a
    // tier is how the AI is told how good they are. The player has no tier:
    // whatever is holding their controller is not this file's business.
    if (entry.player) continue;
    if (!tierNames().includes(entry.tier)) {
      throw new LadderConfigError(
        `"${entry.id}" fights at "${entry.tier}"; there is ${tierNames().join(', ')}`
      );
    }
  }

  if (!seen.has(playerId)) {
    throw new LadderConfigError(`playerId "${playerId}" is not in the roster`);
  }
  const players = roster.filter((entry) => entry.player);
  if (players.length !== 1 || players[0].id !== playerId) {
    throw new LadderConfigError('exactly one roster entry must be the player, and it must be playerId');
  }

  // The player and the opponent must never be the same colour, and the
  // opponent is anybody on this list — so every one of them is checked against
  // the player here, once, at boot, rather than at each booking.
  const you = players[0];
  for (const entry of roster) {
    if (entry === you) continue;
    const distance = tintDistance(you.tint, entry.tint);
    if (distance < MIN_TINT_DISTANCE) {
      throw new LadderConfigError(
        `"${entry.id}" wears ${entry.tint}, ΔE ${distance.toFixed(1)} from the player's ${you.tint}; ` +
          `want at least ${MIN_TINT_DISTANCE} so the two corners can be told apart`
      );
    }
  }

  const threshold = contender?.rank;
  if (!Number.isInteger(threshold) || threshold < 1) {
    throw new LadderConfigError(`contender.rank is ${JSON.stringify(threshold)}; want a rank`);
  }
  // A threshold that admits the whole roster is not a gate, it is a decoration.
  if (threshold >= roster.length) {
    throw new LadderConfigError(
      `contender.rank ${threshold} admits the whole roster of ${roster.length}; nothing would be gated`
    );
  }
  // Counted through the fighters who are actually on the ladder at the start.
  // Anybody who arrives partway through (`debut`) is not standing in front of
  // the player on the opening night and must not be counted as though they are.
  const start = roster.filter((entry) => !entry.debut).findIndex((entry) => entry.id === playerId) + 1;
  // And a player who starts inside it has no circuit to climb, which is the
  // other half of the same mistake.
  if (start <= threshold) {
    throw new LadderConfigError(
      `the player starts at ${start}, already inside the contender threshold of ${threshold}`
    );
  }
  if (!contender.describe) throw new LadderConfigError('contender.rank does not say what it means');

  if (!Number.isInteger(act?.rank ?? 1) || (act?.rank ?? 1) < 1) {
    throw new LadderConfigError(`act.rank is ${JSON.stringify(act?.rank)}; want a rank`);
  }
  if (!act?.id || !act?.name) throw new LadderConfigError('the act needs an id and a name');

  if (!Number.isInteger(movement?.slip) || movement.slip < 1) {
    throw new LadderConfigError(`movement.slip is ${JSON.stringify(movement?.slip)}; want at least 1`);
  }

  if (!(churn?.every > 0)) {
    throw new LadderConfigError(`churn.every is ${JSON.stringify(churn?.every)}; want seconds`);
  }
  if (!Number.isInteger(churn?.spread) || churn.spread < 1) {
    throw new LadderConfigError(`churn.spread is ${JSON.stringify(churn?.spread)}; want at least 1`);
  }
  // Below a half, the worse fighter wins more often than not and the ladder
  // shuffles itself backwards. That is not form, it is noise with a name.
  if (!(churn?.form >= 0.5 && churn.form <= 1)) {
    throw new LadderConfigError(`churn.form is ${JSON.stringify(churn?.form)}; want 0.5..1`);
  }

  if (!Number.isInteger(campaign?.history) || campaign.history < 1) {
    throw new LadderConfigError(`campaign.history is ${JSON.stringify(campaign?.history)}`);
  }

  validateDebut(config);

  // Where everybody lives and who holds each room (`venues.js`). A ladder with
  // `venues: null` is fought nowhere in particular — the unit fixtures, which
  // count ranks and nothing else.
  if (config.venues) validateVenues({ venues: config.venues, takings: config.takings }, { roster });

  return config;
}

/**
 * The fighter who is not there yet, and the two speeds he climbs at.
 *
 * Split out of `validateLadder` because it is a rule about a *pace* rather than
 * about a roster, and the interesting checks are the ones that are not types:
 * a debut who climbs no faster than the churn is a fighter nobody would notice
 * arriving, and a second gear that is not faster than the first is a second
 * gear that does nothing. Both are things somebody would edit the file into by
 * accident, and neither would throw anywhere near the file.
 */
export function validateDebut(config = LADDER) {
  const { debut, roster, churn, playerId, contender } = config ?? {};

  const late = (roster ?? []).filter((entry) => entry.debut);
  if (!debut) {
    if (late.length) {
      throw new LadderConfigError(
        `"${late[0].id}" is marked as a debut, but nothing says when they arrive`
      );
    }
    return config;
  }

  if (late.length !== 1 || late[0].id !== debut.id) {
    throw new LadderConfigError(
      `debut.id is "${debut.id}"; the roster marks ${JSON.stringify(late.map((e) => e.id))}`
    );
  }
  if (late[0].id === playerId) {
    throw new LadderConfigError('the player cannot be the fighter who arrives late');
  }

  // The trigger is the contender gate rather than a second threshold of its
  // own. Two numbers that have to agree are two numbers that will one day
  // disagree, and the gate is already in the file (`contender.rank`).
  if (debut.on !== 'contender') {
    throw new LadderConfigError(`debut.on is ${JSON.stringify(debut.on)}; there is "contender"`);
  }
  if (!contender?.rank) throw new LadderConfigError('a debut waits on a contender threshold');

  const gear = (name, pace, { partial = false } = {}) => {
    if (!pace || typeof pace !== 'object') {
      throw new LadderConfigError(`debut.${name} is ${JSON.stringify(pace)}; want a pace`);
    }
    if (pace.every !== undefined || !partial) {
      if (!(pace.every > 0)) {
        throw new LadderConfigError(`debut.${name}.every is ${JSON.stringify(pace.every)}; want seconds`);
      }
    }
    if (pace.spread !== undefined || !partial) {
      if (!Number.isInteger(pace.spread) || pace.spread < 1) {
        throw new LadderConfigError(`debut.${name}.spread is ${JSON.stringify(pace.spread)}; want at least 1`);
      }
    }
    if (pace.form !== undefined || !partial) {
      if (!(pace.form >= 0.5 && pace.form <= 1)) {
        throw new LadderConfigError(`debut.${name}.form is ${JSON.stringify(pace.form)}; want 0.5..1`);
      }
    }
  };
  gear('climb', debut.climb);
  gear('crowned', debut.crowned, { partial: true });

  // He climbs faster than the churn. That is the whole of what he is, and a
  // file that says otherwise has described an ordinary fighter with extra
  // machinery behind him.
  if (!(debut.climb.every < churn.every)) {
    throw new LadderConfigError(
      `debut.climb.every (${debut.climb.every}s) is not faster than the churn (${churn.every}s)`
    );
  }

  // And faster again once the player is holding the top. Not merely different:
  // at least one of the two numbers has to be better and neither may be worse,
  // or the second gear is a change of pace rather than an increase.
  const second = { ...debut.climb, ...debut.crowned };
  if (second.every > debut.climb.every || second.form < debut.climb.form) {
    throw new LadderConfigError('debut.crowned is slower than debut.climb; it is meant to be faster');
  }
  if (second.every === debut.climb.every && second.form === debut.climb.form) {
    throw new LadderConfigError('debut.crowned climbs at exactly debut.climb; it would do nothing');
  }

  return config;
}

/**
 * What a screen may say about somebody's styles, and when.
 *
 * Junior's are hidden until he walks out. That is a rule about a fighter rather
 * than about a screen, so it lives here with the fighter and each screen says
 * *which* screen it is rather than deciding what the answer should be — the
 * walkout passes `atTheWalkout`, the offer screen does not, and neither of them
 * holds a copy of the rule. A second reader that forgets gets the concealed
 * answer, which is the safe way round.
 *
 * `concealed` is returned rather than left to be inferred from an empty list,
 * because "we are not telling you" and "nothing" are different sentences and a
 * caller printing an empty string for the first one has thrown the mechanic
 * away.
 */
export function whatTheyFight(entry, { atTheWalkout = false } = {}) {
  if (!entry) return Object.freeze({ styles: [], concealed: false });
  const concealed = Boolean(entry.hidden) && !atTheWalkout;
  return Object.freeze({
    styles: concealed ? [] : [...(entry.styles ?? [])],
    concealed,
  });
}

/**
 * The ladder as the game reads it: the roster out of `ladder.json`, and the
 * venues out of `venues.json`. Two files because they are two things — who
 * these people are, and the rooms they fight in — and one config because a
 * fighter's `home` is only checkable against the rooms.
 */
const LADDER = { ...FILE, venues: VENUES.venues, takings: VENUES.takings };

// A roster that cannot be satisfied fails at boot rather than at the first
// fight it ruins.
validateLadder(LADDER);

export { LADDER };

export function createLadder({ config = LADDER, random = Math.random } = {}) {
  validateLadder(config);
  const playerId = config.playerId;

  /** Everybody, by id. The record lives here; the order lives in `order`. */
  const byId = new Map();
  /** Ids, best first. Index 0 is rank 1. This is the ladder. */
  let order = [];

  /** The fighter who is not there yet, or null in a roster with nobody late. */
  const debut = config.debut ?? null;
  /** Whether he has arrived. The saved `order` is the authority on a reload. */
  let debuted = false;

  /** Who holds which room. Null for a ladder fought nowhere in particular. */
  const venues = config.venues
    ? createVenues({ config: { venues: config.venues, takings: config.takings }, roster: config.roster, playerId })
    : null;

  function seed() {
    byId.clear();
    order = [];
    for (const entry of config.roster) {
      byId.set(entry.id, {
        id: entry.id,
        name: entry.name,
        borough: entry.borough,
        /** The colour they wear in the ring. See `MIN_TINT_DISTANCE`. */
        tint: entry.tint ?? null,
        /** What they wear, `{ slot: tier }`, out of the shop's own tiers. */
        gear: entry.gear ? { ...entry.gear } : null,
        /**
         * The body they wear: their mesh in `src/fighters/meshes.json`, or the
         * default. Not a field in `ladder.json` — which file is whose lives in
         * the manifest, beside the files — but carried on the record so that
         * `bookFighter` dresses a fighter in it the same way it dresses them in
         * everything else.
         */
        mesh: meshFor(entry.id),
        styles: [...entry.styles],
        tier: entry.tier ?? null,
        spread: entry.spread ? { ...entry.spread } : null,
        /** What they do with the arena, or null for the twelve who do nothing. */
        ringcraft: entry.ringcraft ? { ...entry.ringcraft } : null,
        player: Boolean(entry.player),
        /** Whether a card may say what they fight before they walk out. */
        hidden: Boolean(entry.hidden),
        record: { wins: 0, losses: 0, draws: 0 },
      });
      // Everybody is *known* from the start — their name and styles come out of
      // the file, and nothing about a fighter is invented at the moment they
      // turn up. What a debut does not have yet is a **rank**, which is what
      // being on the ladder is.
      if (!entry.debut) order.push(entry.id);
    }
    debuted = false;
  }
  seed();

  let clock = 0;
  /** Junior's own clock. He is not in the churn; he has a rule of his own. */
  let climbClock = 0;
  /**
   * Whoever is on the player's card tonight. Not booked across town as well:
   * before the rooms this only meant a rank could move under a fight in
   * progress, and now it would mean a room on the line in the player's fight
   * changing hands in somebody else's, halfway through the round.
   */
  let engaged = null;
  const listeners = new Set();
  const arrivals = new Set();

  const indexOf = (id) => order.indexOf(id);
  const rankOf = (id) => {
    const at = indexOf(id);
    return at < 0 ? null : at + 1;
  };

  /**
   * One fighter as everything outside reads them: the record, plus where they
   * live and what they hold **now**. The two venue fields are worked out on
   * every read rather than stored on the record, because the room changes
   * hands in `settle` and a copy on the record would be a second answer.
   */
  const view = (id, rank = rankOf(id)) => ({
    ...byId.get(id),
    rank,
    /** The venue they live in: the roster's, or for the player what they took last. */
    home: venues?.homeOf(id) ?? null,
    /** Every room they hold, in the file's order. Empty for most of them. */
    holds: venues?.held(id) ?? [],
  });

  /** A venue as a screen wants it: the file's fields, and who holds it now. */
  function venueView(venueId) {
    const venue = venues?.get(venueId);
    if (!venue) return null;
    const holder = venues.holderOf(venueId);
    return Object.freeze({
      id: venue.id,
      name: venue.name,
      arena: venue.arena,
      where: venue.where,
      holder,
      holderName: byId.get(holder)?.name ?? null,
      /** Who lives there, best-ranked first. The holder need not be one of them. */
      residents: config.roster
        .filter((entry) => !entry.player && entry.home === venue.id && rankOf(entry.id) !== null)
        .map((entry) => entry.id)
        .sort((a, b) => rankOf(a) - rankOf(b)),
    });
  }

  /** Where two fighters would fight, as a venue id. See `venues.js`. */
  const whereFor = (a, b) => venues?.where(a, b, rankOf) ?? null;

  /**
   * One night's money, for **anybody**: both fighters paid their share of the
   * purse (the bank's own `pay` shares — the player's real cash is the bank's
   * to hand over; this is the ledger everybody has, the player included), both
   * of them collecting their room tabs because they turned up, and then the
   * room's cut of tonight's purse onto its holder's tab. Collected first, so
   * tonight's cut is the next fight's money: "between fights".
   *
   * `purse` is the card's for the player's fight, and for everybody else's
   * what a card at that height would pay (`purseFor`, at the resting heat) —
   * the same money, so the city's economy is the player's rather than a copy.
   */
  function payNight({ a, b, outcome, at, ranks, purse = null }) {
    if (!venues) return { purse: null, collected: {}, venue: null, winner: null, loser: null };
    const money =
      purse ?? purseFor({ rank: Math.min(ranks[0] ?? order.length, ranks[1] ?? order.length), size: order.length });
    const share = (result) => Math.round(money * BANK.pay[result]);
    venues.paid(a, share(outcome === 'draw' ? 'draw' : 'win'));
    venues.paid(b, share(outcome === 'draw' ? 'draw' : 'loss'));
    const collected = { [a]: venues.collect(a), [b]: venues.collect(b) };
    return { purse: money, collected };
  }

  /** Takes `id` out of the order and puts them back at `rank`. */
  function place(id, rank) {
    const from = indexOf(id);
    if (from < 0) return null;
    const to = Math.min(Math.max(rank, 1), order.length) - 1;
    if (to === from) return from + 1;
    order.splice(from, 1);
    order.splice(to, 0, id);
    return to + 1;
  }

  function announce(result) {
    for (const listener of listeners) listener(result);
    return result;
  }

  /** Whether the player is inside the contender threshold right now. */
  const inContention = () => {
    const at = rankOf(playerId);
    return at !== null && at <= config.contender.rank;
  };
  /** And whether they are holding the top of it. */
  const crowned = () => rankOf(playerId) === 1;

  /**
   * Junior turns up.
   *
   * **At the bottom**, the moment the player first crosses into contention —
   * not on a timer, and not at a rank that flatters him. He has the record of
   * somebody who has never fought here, because he has not, and the first thing
   * that happens to him is that he starts winning.
   *
   * The trigger is the contender gate the campaign already reads rather than a
   * second threshold in the file, so the fighter who arrives and the fight the
   * player has just unlocked are the same moment by construction.
   *
   * Once, ever. `debuted` says so, and a career loaded out of a save says so by
   * having him in the order already.
   */
  function arrive() {
    if (!debut || debuted) return null;
    const entry = byId.get(debut.id);
    if (!entry) return null;
    debuted = true;
    climbClock = 0;
    order.push(debut.id);
    const event = Object.freeze({
      id: debut.id,
      name: entry.name,
      rank: order.length,
      /** What the player did to bring him out. */
      because: debut.on,
    });
    for (const listener of arrivals) listener(event);
    return event;
  }

  /** The one place that asks whether it is time. Cheap, and safe to spam. */
  function watchForDebut() {
    if (!debut || debuted || !inContention()) return null;
    return arrive();
  }

  /**
   * How fast he is climbing right now.
   *
   * Two gears. The second is a partial override of the first, so a file that
   * only wants to change the interval says only the interval — and a number
   * that is the same in both gears is written once.
   */
  function pace() {
    return crowned() ? { ...debut.climb, ...debut.crowned } : debut.climb;
  }

  /**
   * Who Junior fights on his own clock: somebody **above** him, within reach.
   *
   * Upwards only, and that is the mechanism rather than a flavour choice. A win
   * against somebody below him takes no rank — `settle` only moves the winner
   * when the loser was above them — so a climb made of fights in both
   * directions is a fighter standing still while his record grows.
   *
   * Never the player. His clock is the rest of the city; the player's fights
   * are the ones being played.
   */
  function climbPairing() {
    const at = indexOf(debut.id);
    if (at < 0) return null;
    const reach = pace().spread;
    const above = order
      .slice(Math.max(0, at - reach), at)
      .filter((id) => id !== playerId && id !== engaged);
    if (!above.length) return null;
    return above[Math.floor(random() * above.length) % above.length];
  }

  /**
   * One result, settled.
   *
   * **The winner takes the loser's rank if the loser was above them**, and
   * everybody in between shifts down one — you climb past the person you beat
   * and nobody else is punished for it. If the winner was *already* above, they
   * have nothing to take, so the loser slips instead: a result has to cost the
   * loser something or a champion could farm the bottom of the ladder forever
   * and a beaten fighter would keep their place for losing.
   *
   * Symmetric by construction, which is the point. This is the only function
   * that reorders anything, and it does not know or care whether either fighter
   * is the player.
   */
  function settle(winnerId, loserId, { at = null, purse = null } = {}) {
    const winner = byId.get(winnerId);
    const loser = byId.get(loserId);
    if (!winner || !loser || winnerId === loserId) return null;

    const was = { winner: rankOf(winnerId), loser: rankOf(loserId) };
    winner.record.wins += 1;
    loser.record.losses += 1;

    if (was.winner > was.loser) {
      place(winnerId, was.loser);
    } else {
      place(loserId, was.loser + config.movement.slip);
    }

    // The money, then the room: the purse paid, both tabs collected, tonight's
    // cut to whoever holds the room — and the room itself, if it was the
    // loser's to lose. Same rules whoever these two are.
    const night = payNight({ a: winnerId, b: loserId, outcome: 'win', at, ranks: [was.winner, was.loser], purse });
    const venue = at && venues ? venues.fought(at, { winner: winnerId, loser: loserId, purse: night.purse }) : null;

    // Ranks have moved, so the player may have just crossed. Checked here
    // rather than in `update` alone, because a result is the only thing that
    // reorders anything and the player's own fight does not go through a tick.
    watchForDebut();

    return announce({
      winner: winnerId,
      loser: loserId,
      was,
      now: { winner: rankOf(winnerId), loser: rankOf(loserId) },
      /** Whether the win actually took a place, or only cost the loser one. */
      took: was.winner > was.loser,
      /** Where it was fought, or null on a ladder fought nowhere in particular. */
      at: at ?? null,
      /** `{ id, from, to }` when the room changed hands in it, else null. */
      venue,
      /** What the fight paid, and what each of them collected off their tabs. */
      purse: night.purse,
      collected: night.collected,
      /** True when neither fighter is the player: this happened off-screen. */
      background: winnerId !== playerId && loserId !== playerId,
    });
  }

  /**
   * Two fighters near each other in the ladder, neither of them the player.
   *
   * Near each other because a ladder where rank 12 is regularly matched with
   * rank 1 is not a ladder, it is a raffle; and never the player because the
   * player's fights are the ones being played.
   */
  function pairOff() {
    // Never the player, and never Junior. His ascent is his own rule on his own
    // clock (see `climbPairing`); leaving him in the churn as well would have
    // the two rules pulling against each other — the churn favours the
    // better-ranked fighter, which early on is whoever he is standing under.
    const candidates = order.filter((id) => id !== playerId && id !== debut?.id && id !== engaged);
    if (candidates.length < 2) return null;

    const first = candidates[Math.floor(random() * candidates.length) % candidates.length];
    const at = candidates.indexOf(first);
    const near = candidates.filter(
      (id, index) => id !== first && Math.abs(index - at) <= config.churn.spread
    );
    if (!near.length) return null;
    const second = near[Math.floor(random() * near.length) % near.length];
    return [first, second];
  }

  return {
    config,
    playerId,

    /** The ladder, best first, each with the rank they currently hold. */
    get ranks() {
      return order.map((id, index) => view(id, index + 1));
    },
    get size() {
      return order.length;
    },
    rankOf,
    /**
     * One fighter, with their rank — or null for somebody who is not on the
     * ladder yet. A debut is *known* before he arrives (his name and styles are
     * in the file) and he is not findable, because being findable is what
     * having a rank means and everything that looks a fighter up is about to
     * put them in a fight.
     */
    find: (id) => (byId.has(id) && rankOf(id) !== null ? view(id) : null),

    /** The fighter who arrives partway through, and whether he has. */
    get debut() {
      if (!debut) return null;
      const entry = byId.get(debut.id);
      return Object.freeze({
        id: debut.id,
        name: entry?.name ?? null,
        arrived: debuted,
        rank: rankOf(debut.id),
        /** Which gear he is climbing in, or null before he is here. */
        pace: debuted ? Object.freeze({ ...pace(), crowned: crowned() }) : null,
      });
    },
    /** Whoever holds `rank`, 1-based. */
    at: (rank) => (order[rank - 1] ? view(order[rank - 1], rank) : null),
    /** The player's entry, which is the one thing everything else asks for. */
    get player() {
      return view(playerId);
    },

    /** Every venue, in the file's order, with whoever holds it now. Empty without any. */
    get venues() {
      return venues ? venues.list.map((venue) => venueView(venue.id)) : [];
    },
    /** One venue, or null. */
    venue: (id) => venueView(id),
    /** What a room pays, `{ cut, tab }`, or null on a ladder whose rooms pay nothing. */
    get takings() {
      return venues?.takings ?? null;
    },
    /** Everything `id` has been paid: `{ purses, rooms }`. The city's ledger, the player's included. */
    earnings: (id) => venues?.earnings(id) ?? Object.freeze({ purses: 0, rooms: 0 }),
    /** What `id` is owed out of their rooms and has not collected: `[{ id, name, amount, fights }]`. */
    owed: (id) => venues?.owed(id) ?? [],
    /**
     * Where `a` and `b` would fight tonight: the higher-ranked fighter's room.
     * Null on a ladder with no venues.
     */
    venueFor: (a, b) => venueView(whereFor(a, b)),
    /** How far off the top the player is. Zero means they are it. */
    get climb() {
      return rankOf(playerId) - 1;
    },

    /**
     * Who the player says they are.
     *
     * The **one** identity in this game that does not come out of `ladder.json`,
     * and the exception is the whole of why the create screen (step 67) can
     * exist. Gotcha 60's rule — a save owns state, never identity — is about
     * the *roster*: names and styles are the file's so that editing it changes
     * an existing career rather than being shadowed by it. Nobody edits a name
     * a person typed, and there is no file answer to fall back to, so this one
     * is the player's and the save carries it.
     *
     * It is the entry that changes, not the combatant. Everything downstream —
     * `bookFighter`, the walkout card, Sala, the hub — reads the roster entry,
     * so putting it here is what makes one call enough.
     */
    identify(id, { name = null, styles = null } = {}) {
      const entry = byId.get(id);
      if (!entry) return null;
      if (typeof name === 'string' && name.trim()) entry.name = name.trim();
      if (Array.isArray(styles) && styles.length) {
        for (const style of styles) {
          if (!STYLES.styles[style]) {
            throw new LadderConfigError(`"${style}" is not a style; nobody can be built fighting it`);
          }
        }
        if (styles.length > STYLES.maxStyles) {
          throw new LadderConfigError(
            `${styles.length} styles; the cap is ${STYLES.maxStyles}`
          );
        }
        entry.styles = [...styles];
      }
      return { ...entry, rank: rankOf(id) };
    },

    settle,

    /**
     * Who is on the player's card tonight, so the city does not book them too.
     * Set when a fight is booked; null for nobody. Not saved: a reload books
     * the next fight afresh, and this with it.
     */
    engage(id) {
      engaged = id && byId.has(id) ? id : null;
      return engaged;
    },
    get engaged() {
      return engaged;
    },

    /**
     * A fight nobody won. Both records show it and **nobody moves**.
     *
     * A draw is not half a win: there is no rank to take, because neither of
     * them beat the other. What it is not, either, is nothing — it goes on both
     * records, or a fighter who drew eleven times reads as though they had a
     * night off.
     */
    drew(a, b, { at = null, purse = null } = {}) {
      const one = byId.get(a);
      const two = byId.get(b);
      if (!one || !two || a === b) return null;
      one.record.draws += 1;
      two.record.draws += 1;
      // Paid and collected like any night. The room hosted a fight and takes
      // its cut; nothing else: a draw takes nothing off anybody.
      const night = payNight({ a, b, outcome: 'draw', at, ranks: [rankOf(a), rankOf(b)], purse });
      if (at && venues) venues.fought(at, { purse: night.purse });
      // Nobody moved, so nothing has crossed — but this is a public door and a
      // rank the player reached through somebody else's result is a rank
      // nothing has looked at since. Idempotent, so the cost of asking is nil.
      watchForDebut();
      return announce({
        winner: null,
        loser: null,
        drawn: [a, b],
        was: { winner: rankOf(a), loser: rankOf(b) },
        now: { winner: rankOf(a), loser: rankOf(b) },
        took: false,
        at: at ?? null,
        venue: null,
        purse: night.purse,
        collected: night.collected,
        background: a !== playerId && b !== playerId,
      });
    },

    /**
     * Time passing, which the rest of the ladder spends fighting.
     *
     * Runs on the same clock the fight does, so a long round is a night in
     * which other people's results come in — the ladder the player climbs back
     * onto is not the one they left.
     */
    update(dt) {
      if (!(dt > 0)) return [];
      // A background result may have dropped somebody past the player while
      // nothing was looking, so the gate is read before the tick as well as
      // after every result.
      watchForDebut();
      clock += dt;
      const results = [];

      // Junior's own clock, and it runs faster than the churn does — that is
      // the whole of what he is. Faster again once the player is holding the
      // top, because the thing arriving behind you is supposed to be arriving.
      // Not while he is on the player's card: he is busy.
      if (debut && debuted && engaged !== debut.id) {
        climbClock += dt;
        let rate = pace();
        while (climbClock >= rate.every) {
          climbClock -= rate.every;
          const foe = climbPairing();
          if (!foe) continue;
          // Form, not certainty, same as the churn — he loses sometimes, and a
          // fighter who cannot lose is a cutscene.
          const beaten = random() < rate.form;
          // He goes to them: everybody he fights is above him, so they host.
          const at = whereFor(debut.id, foe);
          results.push(
            beaten ? settle(debut.id, foe, { at }) : settle(foe, debut.id, { at })
          );
          // Re-read between fights: winning one of these can be what puts the
          // player's rank where the second gear starts.
          rate = pace();
        }
      }

      while (clock >= config.churn.every) {
        clock -= config.churn.every;
        const pair = pairOff();
        if (!pair) continue;
        const [a, b] = pair;
        // Form, not certainty: the better-ranked fighter is favoured and does
        // not always win, which is the only reason the order ever changes.
        const favourite = rankOf(a) < rankOf(b) ? a : b;
        const outsider = favourite === a ? b : a;
        const upset = random() >= config.churn.form;
        results.push(
          settle(upset ? outsider : favourite, upset ? favourite : outsider, { at: whereFor(a, b) })
        );
      }
      return results;
    },

    /** Seconds until the next card somewhere else in the city. */
    get nextCard() {
      return Math.max(0, config.churn.every - clock);
    },

    onResult(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /**
     * Somebody arrived. Its own hook rather than a shape on `onResult`, because
     * a debut is not a result — nobody fought, nobody's record moved, and the
     * thing listening for a background result would have to learn to ignore it.
     */
    onDebut(listener) {
      arrivals.add(listener);
      return () => arrivals.delete(listener);
    },

    /** Back to the opening order, records and all — and Junior back offstage. */
    reset() {
      seed();
      venues?.reset();
      engaged = null;
      clock = 0;
      climbClock = 0;
      return order.length;
    },

    /**
     * The ladder as plain data: who is where, and what each of them has done.
     *
     * Only the *mutable* half. Names, styles, tiers and spreads are not in here
     * — they come out of `ladder.json`, and a save that carried its own copy
     * would quietly keep fighting last week's roster after the file changed.
     * What a save owns is the order and the records; what the file owns is who
     * these people are.
     */
    snapshot() {
      return {
        order: [...order],
        records: Object.fromEntries(
          order.map((id) => [id, { ...byId.get(id).record }])
        ),
        /** Who holds each room, and who is owed what out of it. */
        venues: venues?.snapshot() ?? null,
        /** What everybody has been paid, purses and rooms. */
        earnings: venues?.snapshotEarnings() ?? null,
      };
    },

    /**
     * Puts a saved ladder back, or refuses the whole thing.
     *
     * **All or nothing.** A saved order that does not name exactly the fighters
     * the file declares is not a ladder with a fighter missing; it is a save
     * from a different roster, and applying the half of it that matches would
     * leave the game in a state neither the file nor the save describes.
     */
    restore(saved) {
      const ids = saved?.order;
      if (!Array.isArray(ids) || !ids.length) return false;
      if (ids.some((id) => !byId.has(id)) || new Set(ids).size !== ids.length) return false;

      // Everybody who is on the ladder at the start has to be in the saved
      // order, and the **only** id that may be missing from it or extra in it
      // is the one the file says arrives partway through. A save naming anybody
      // else, or short of anybody else, is a save from a different roster — and
      // a career whose length is simply "13 or 14" would let a roster edit
      // through that this check exists to catch.
      const named = new Set(ids);
      for (const entry of config.roster) {
        if (entry.debut) continue;
        if (!named.has(entry.id)) return false;
      }
      for (const id of named) {
        if (id === debut?.id) continue;
        if (!config.roster.some((entry) => entry.id === id && !entry.debut)) return false;
      }

      // The rooms, last of the checks and before anything is applied: a save
      // missing a venue, naming one that no longer exists, or handing one to
      // somebody who is not on its ladder is from a different circuit. Refused
      // whole, like the order.
      if (venues && !venues.restore(saved.venues, (id) => named.has(id))) return false;
      venues?.restoreEarnings(saved.earnings);

      order = [...ids];
      // The saved order is the authority on whether he has turned up. It is a
      // fact about the career rather than a flag to carry next to one.
      debuted = Boolean(debut && named.has(debut.id));
      for (const [id, record] of Object.entries(saved.records ?? {})) {
        const entry = byId.get(id);
        if (!entry) continue;
        entry.record = {
          wins: Number(record?.wins) || 0,
          losses: Number(record?.losses) || 0,
          draws: Number(record?.draws) || 0,
        };
      }
      clock = 0;
      climbClock = 0;
      // A career saved on the night the player became a contender can come back
      // in contention with nobody behind them yet. Idempotent, and it is the
      // same door every other path goes through.
      watchForDebut();
      return true;
    },
  };
}
